import { Component, ElementRef, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { filter, map, mergeMap, tap, Subscription } from 'rxjs';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { bodyCssClassPropertyName } from '@app/app-routing.module';
import { SpinnerService } from '@app/shared/services/spinner/spinner.service';
import { CookieConsentPopupService } from '@app/shared/services/cookie-consent-popup/cookie-consent-popup.service';
import { TranslateService } from '@ngx-translate/core';
import { ThemeService } from "@app/salon-details/services/theme.service";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnDestroy, OnInit {
  showSpinner = false;

  private themeService = inject(ThemeService);
  private previousBodyCssClass: string;
  private masterSubscription = new Subscription();
  private spinnerTimeoutId: number;
  private readonly defaultBodyCssClass = 'bjootify';

  constructor(
    private spinnerService: SpinnerService,
    private activatedRoute: ActivatedRoute,
    private cookieConfigService: CookieConsentPopupService,
    private renderer: Renderer2,
    private router: Router,
    public el: ElementRef,
    public translate: TranslateService) {
    this.setupSpinner();
    this.setupBodyCssClass();
    this.cookieConfigService.initialize();
    this.setupHtmlLangClass();
  }

  ngOnDestroy(): void {
    this.masterSubscription.unsubscribe();
  }

  ngOnInit(): void {
    this.themeService.useDefaultTheme();
  }

  isUnsupportedBrowser(): boolean {
    const IOSGoogleSearchAppUA = 'GSA';
    return navigator.userAgent.includes(IOSGoogleSearchAppUA);
  }

  private setupHtmlLangClass(): void {
    this.updateLanguage();
    const htmlLangChangeSub = this.translate.onLangChange
      .subscribe(() => {
        this.updateLanguage();
      });
    this.masterSubscription.add(htmlLangChangeSub);
  }

  private setupBodyCssClass(): void {
    const bodyCssClassSub = this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map(route => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        mergeMap(route => route.data),
        map(data => Object.prototype.hasOwnProperty.call(data, bodyCssClassPropertyName) ? data.bodyCssClass : this.defaultBodyCssClass),
        tap(bodyCssClass => {
          if (this.previousBodyCssClass) {
            this.renderer.removeClass(document.body, this.previousBodyCssClass);
          }
          this.renderer.addClass(document.body, bodyCssClass);
          this.previousBodyCssClass = bodyCssClass;
        }))
      .subscribe();
    this.masterSubscription.add(bodyCssClassSub);
  }

  private setupSpinner(): void {
    const spinnerSub = this.spinnerService.status$
      .subscribe((status: boolean) => {
        clearTimeout(this.spinnerTimeoutId);
        if (status) {
          this.showSpinner = true;
          return;
        }
        this.spinnerTimeoutId = setTimeout(() => {
          this.showSpinner = false;
        }, 500) as any;
      });
    this.masterSubscription.add(spinnerSub);
  }

  private updateLanguage(): void {
    const lang = document.createAttribute('lang');
    const xmllang = document.createAttribute('xml:lang');
    lang.value = this.translate.currentLang;
    xmllang.value = this.translate.currentLang;
    this.el.nativeElement.parentElement.parentElement.attributes.setNamedItem(lang);
    this.el.nativeElement.parentElement.parentElement.attributes.setNamedItem(xmllang);
  }

}
